<template>
  <v-alert
    v-if="authStore.isPartner"
    class="buyout__emailBox vAlert--success"
  >
    {{ mainBuyoutStep === 2 && personalDataStep === 1 ? t('buyout.appraisal.handover_to_client') : t('buyout.appraisal.or_handover_to_client') }}
    <div class="app__form">
      <div class="formItem formItem--1x12 formItem--btn">
        <v-text-field
          v-model="clientEmail"
          :placeholder="t('buyout.appraisal.recipient_email')"
          required
          :error-messages="clientEmailErrorMessage"
          :rules="[rules.required, rules.isEmail]"
          type="email"
          name="recipient_email"
          @change="clientEmailErrorMessage=''"
        />
        <client-only>
          <v-btn
            class="mbtn mbtn--green"
            size="large"
            :disabled="props.disableSend || (buyoutStore.selectedDeliveryType?.identifier === 'cashtec_samsung_service_store_handover')"
            :loading="loadingEmailSend"
            @click="handleSendAction"
          >
            {{ t('buyout.appraisal.send') }}
          </v-btn>
        </client-only>
      </div>
    </div>
  </v-alert>
</template>

<script setup lang="ts">
import { storeToRefs } from "pinia";

const props = withDefaults(defineProps<{
  moveToBonusStepFn?:() => Promise<boolean>
  disableSend?: boolean,
}>(), {
  disableSend: false,
  moveToBonusStepFn: async () => false
});

const { t } = useI18n();
const { rules } = useFormValidators();
const authStore = useAuthStore();
const api = useApi();
const buyoutStore = useBuyoutStore();
const bonusPartnerWithManufacturerBuyoutStore = useBonusPartnerWithManufacturerBuyoutStore();
const { notify } = useNotification();
const { mainBuyoutStep, clientEmail, userBuyoutCode, appraisalBuyoutStep, personalDataStep } = storeToRefs(buyoutStore);
const { newDeviceImei, selectedNewDevicePartner, takeNewDeviceStep, newDeviceBonusStep } = storeToRefs(bonusPartnerWithManufacturerBuyoutStore);
const clientEmailErrorMessage = ref<string>("");

const loadingEmailSend = ref<boolean>(false);
const emailLowercase = computed<string>(() => clientEmail.value.toLowerCase());

async function handleSendAction() {
  if(newDeviceBonusStep.value === 2){
    if(mainBuyoutStep.value === 0){
      await createNewDevice();
    }
    else if(appraisalBuyoutStep.value === 5) {
      if(mainBuyoutStep.value === 1) {
        await updateBuyout();
      }
      else if (mainBuyoutStep.value === 3 && personalDataStep.value == 2){
        await updateBuyout();
      }
    }
  }
}

async function updateBuyout () {
  loadingEmailSend.value = true;
  takeNewDeviceStep.value = false;
  if(await bonusPartnerWithManufacturerBuyoutStore.moveToPersonalData()){
    await sendToClient(emailLowercase.value, userBuyoutCode.value);
  }
  loadingEmailSend.value = false;
}

async function createNewDevice () {
  loadingEmailSend.value = true;
  if (!newDeviceImei.value || !selectedNewDevicePartner.value) {
    await sendToClient(emailLowercase.value);
  } else {
    takeNewDeviceStep.value = false;
    if(await props.moveToBonusStepFn()) {
      await sendToClient(emailLowercase.value, userBuyoutCode.value);
    } else {
      loadingEmailSend.value = false;
    }
  }
}

async function sendToClient (clientEmailAddress: string, buyoutNumber: string | null = null) {
  loadingEmailSend.value = true;
  // Split to remove partner ref ID in params
  let url = window.location.href.split("?")[0];
  if (buyoutNumber) {
    url = `${url}?userBuyoutCode=${buyoutNumber}&refId=${authStore!.data!.reference_code}&refType=concept_open`;
  } else if (userBuyoutCode?.value) {
    url = `${url}?userBuyoutCode=${userBuyoutCode.value}&refId=${authStore!.data!.reference_code}&refType=concept_open`;
  } else {
    url = `${url}?refId=${authStore!.data!.reference_code}&refType=concept_open`;
  }
  const { error } = await api.call<string>(api.commonUrls.buyout.SEND_TO_CLIENT_URL(), {
    method: "POST",
    body: {
      client_email: clientEmailAddress,
      url
    }
  });
  if (error.value) {
    notify({
      title: t("form.common.error"),
      text: error.value?.data?.detail ?? t("general.error_sending_request"),
      type: "error"
    });
    // buyoutPersonalDataErrors.value = error.value?.data;
  } else {
    notify({
      title: t("buyout.appraisal.send_successful"),
      type: "success"
    });

    takeNewDeviceStep.value = true;
    mainBuyoutStep.value = 3;
  }
  loadingEmailSend.value = false;
}

</script>
